/* eslint-disable default-case, react/prop-types, no-return-assign, react/sort-comp, no-shadow, react/no-did-update-set-state, react/jsx-no-bind, quote-props, react/jsx-closing-bracket-location, consistent-return */
import classnames from 'classnames';
import React, { Component, PropTypes } from 'react';
import Keys from 'es6/core/keys';
import Annotations from 'es6/components/apps/annotations';
import FitText from 'es6/components/common/fit-text';
import { partOfSpeech } from 'es6/helpers/localization';
import Glyph, { JAPANESE_CHARACTER, KEYBOARD, PAPERCLIP } from 'es6/components/common/glyph';
import KanjiStrokes, { containsDisplayableCharacters } from 'es6/components/apps/kanji-strokes';
import { NavButton, Navigation } from 'es6/components/apps/navigation';
import SentenceTab from 'es6/components/apps/sentence-tab';
import SpellingPractice from 'es6/components/apps/spelling-practice';
import SoundPlayer from 'es6/components/common/sound-player';
import SoundManager from 'es6/services/sound-manager';
import { t } from 'es6/services/localization';

// Have a subset of modes for the screen.
export const DEFAULT_MODE = 0;
export const ANIMATION_MODE = 1;
export const ANNOTATION_MODE = 2;
export const KANJI_STROKE_MODE = 3;
export const SENTENCE_MODE = 4;
export const SPELLING_MODE = 5;

// Animation/Sound phases.
const ANIMATION_INITIAL = 0;
const ANIMATION_CUE_INITIAL = 1;
const ANIMATION_PLAY_SOUND_INITIAL = 2;
const ANIMATION_CUE_FADE = 3;
const ANIMATION_SOUND_RESPONSE = 4;
const ANIMATION_RESPONSE_FADE = 5;
const ANIMATION_REVEAL_ALL = 6;
const ANIMATION_PLAY_SOUND = 7;
const ANIMATION_SHADOWING = 8;
const ANIMATION_SHADOWING_FADE = 9;

/**
 * This returns whether or not kanji strokes will be rendered.
 *
 * @this StudyScreen
 * @returns {Boolean}
 */
function kanjiStrokesEnabled() {
  const { itemPackage } = this.props;
  return itemPackage.getLanguage() === 'ja' && containsDisplayableCharacters(itemPackage.getText());
}

/**
 * @this StudyScreen
 */
function showKanjiStrokes() {
  this.setState({ mode: KANJI_STROKE_MODE });
}

/**
 * This returns whether or not annotations will be rendered.
 *
 * @this StudyScreen
 * @returns {Boolean}
 */
function annotationsEnabled() {
  return !!this.props.itemPackage.getAnnotation();
}

/**
 * This returns whether or not fill in the blank will be rendered.
 *
 * @this StudyScreen
 * @returns {Boolean}
 */
function spellingModeEnabled() {
  const { itemPackage } = this.props;
  return !itemPackage.isKanaCourseItem();
}

/**
 * @this StudyScreen
 * @returns {Object}
 */
function getInitialState() {
  return {
    animationPhase: ANIMATION_INITIAL,
    animationRequestId: null,
    mode: this.props.animateOnMount ? ANIMATION_MODE : DEFAULT_MODE,
  };
}

/**
 * @this StudyScreen
 */
function toggleFillInTheBlank() {
  const mode = this.state.mode === SPELLING_MODE ? DEFAULT_MODE : SPELLING_MODE;
  this.setState({ mode });
}

/**
 * @this StudyScreen
 * @param {Event} e
 */
function handleKeydown(e) {
  const { ctrlKey, keyCode, metaKey } = e;
  const { mode } = this.state;
  const { itemPackage } = this.props;
  const isAnimationOrDefault = mode === DEFAULT_MODE || mode === ANIMATION_MODE;

  if (keyCode === Keys.SPACE && (mode === DEFAULT_MODE || mode === SPELLING_MODE) && itemPackage.sound) {
    SoundManager.play(itemPackage.sound);
  }

  if (keyCode === Keys.DELETE && mode !== SPELLING_MODE) {
    this.props.onRemove();
  }

  // Try to switch to stroke mode. Since it is the C key, allow for copying
  // by making sure the user is not holding down on control.
  if (keyCode === Keys.C && !ctrlKey && !metaKey && isAnimationOrDefault && kanjiStrokesEnabled.call(this)) {
    showKanjiStrokes.call(this);
  }

  if (keyCode === Keys.N && isAnimationOrDefault && annotationsEnabled.call(this)) {
    showAnnotations.call(this);
  }

  // Hitting enter stops animation mode.
  // If in spelling mode, hitting enter will not trigger the navigation.
  if (keyCode === Keys.ENTER) {
    if (mode === ANIMATION_MODE) {
      e.preventDefault();
      setDefaultMode.call(this);
    } else if (mode === SPELLING_MODE) {
      e.preventDefault();
    }
  }

  // Hitting escape key will reset the state back to the default if in
  // stroke order or annotation mode.
  if (keyCode === Keys.ESC && (mode === SPELLING_MODE || mode === ANNOTATION_MODE || mode === KANJI_STROKE_MODE)) {
    setDefaultMode.call(this);
  }
}

/**
 * This event needs to be kept separately for opening up the IME.
 * If the IME is enabled with a keydown event, then the keypress event
 * will trigger after and display the character pressed.
 *
 * If the IME is changed to only depend on keydown events, this can also
 * be moved to the keydown handler.
 *
 * @this StudyScreen
 * @param {Event} e
 */
function handleKeypress(e) {
  const { charCode } = e;
  const { mode } = this.state;
  const isAnimationOrDefault = mode === DEFAULT_MODE || mode === ANIMATION_MODE;

  if (String.fromCharCode(charCode) === 's' && isAnimationOrDefault && spellingModeEnabled.call(this)) {
    this.setState({ mode: SPELLING_MODE });
  }
}

/**
 * Callback for clicking the next button.
 *
 * @this StudyScreen
 */
function handleNext() {
  const { mode } = this.state;
  const { onNext } = this.props;

  if (mode === ANIMATION_MODE) {
    setDefaultMode.call(this);
  } else {
    onNext();
  }
}

/**
 * Handles the end of all animations. Most of the callbacks are done
 * through CSS3 animations. However, some are handled by sound player,
 * which will not give an event object. Use the state of the instance
 * to determine what needs to happen next.
 *
 * If you need to change how the transitions behave, make sure this
 * callback is bound to an individual element that takes the longest
 * to transition.
 */
function handleTransitionEnd(e) {
  const { animationPhase, mode } = this.state;

  // All of these events are done through CSS animations and the sound player.
  if (mode === ANIMATION_MODE) {
    const { sound } = this.props.itemPackage;

    switch (animationPhase) {
      // Set the transition to be PLAY_SOUND_INITIAL and play the sound.
      // Otherwise, skip to the ANIMATION_CUE_FADE part of the animation.
      case ANIMATION_CUE_INITIAL:
        if (sound) {
          this.setState({ animationPhase: ANIMATION_PLAY_SOUND_INITIAL });
          SoundManager.play(sound, { finished: handleTransitionEnd.bind(this) });
        } else {
          this.setState({ animationPhase: ANIMATION_CUE_FADE });
        }
        break;

      case ANIMATION_PLAY_SOUND_INITIAL:
        this.setState({ animationPhase: ANIMATION_CUE_FADE });
        break;

      case ANIMATION_CUE_FADE:
        this.setState({ animationPhase: ANIMATION_SOUND_RESPONSE });
        break;

      case ANIMATION_SOUND_RESPONSE:
        this.setState({ animationPhase: ANIMATION_RESPONSE_FADE });
        break;

      case ANIMATION_RESPONSE_FADE:
        this.setState({ animationPhase: ANIMATION_REVEAL_ALL });
        break;

      // Similar to animation ANIMATION_CUE_INITIAL. Try to play the sound
      case ANIMATION_REVEAL_ALL:
        if (sound) {
          this.setState({ animationPhase: ANIMATION_PLAY_SOUND });
          SoundManager.play(sound, { finished: handleTransitionEnd.bind(this) });
        } else {
          this.setState({ animationPhase: ANIMATION_SHADOWING });
        }
        break;

      case ANIMATION_PLAY_SOUND:
        this.setState({ animationPhase: ANIMATION_SHADOWING });
        break;

      case ANIMATION_SHADOWING:
        this.setState({ animationPhase: ANIMATION_SHADOWING_FADE });
        break;

      // The animation is finished, set it back to default.
      case ANIMATION_SHADOWING_FADE:
        this.setState({ mode: DEFAULT_MODE });
        break;
    }
  }
}

/**
 * Navigation component for study screen.
 */
function StudyScreenNavigation({ disableEnterKey, disableArrowKeys, saveNavigationRef, onNext, onRemove }) {
  return (
    <Navigation disableEnterKey={disableEnterKey} disableArrowKeys={disableArrowKeys} ref={saveNavigationRef}>
      <NavButton align="left" onClick={onRemove} title={t('js_iknow.remove')} className="btn-danger btn-nav-icon glyph glyph-remove" />
      <NavButton align="right" focused onClick={onNext}>{t('js_iknow.next')}</NavButton>
    </Navigation>
  );
}

/**
 * Starts the animation if the phase and mode are initialed.
 *
 * @this StudyScreen
 */
function startAnimation() {
  const { animationPhase, mode } = this.state;

  if (mode === ANIMATION_MODE && animationPhase === ANIMATION_INITIAL) {
    this.setState({ animationRequestId: null, animationPhase: ANIMATION_CUE_INITIAL });
  }
}

/**
 * @this StudyScreen
 */
function showAnnotations() {
  this.setState({ mode: ANNOTATION_MODE });
}

/**
 * @this StudyScreen
 */
function showSentences() {
  this.setState({ mode: SENTENCE_MODE });
}

/**
 * This sets the screen to its default mode.
 *
 * @this StudyScreen
 */
function setDefaultMode() {
  this.setState({ mode: DEFAULT_MODE });
}

class StudyScreen extends Component {
  constructor(props) {
    super(props);
    this.state = getInitialState.call(this);

    // Keep a reference to the bound function so it can be unbound when unmounted.
    this.handleKeypress = handleKeypress.bind(this);
    this.handleKeydown = handleKeydown.bind(this);

    this.saveNavigationRef = ref => this.navigationRef = ref;
  }

  componentDidMount() {
    const { itemPackage, playAudioOnMount } = this.props;

    window.addEventListener('keypress', this.handleKeypress);
    window.addEventListener('keydown', this.handleKeydown);
    startAnimation.call(this);

    if (playAudioOnMount) {
      SoundManager.play(itemPackage.sound);
    }
  }

  componentDidUpdate(prevProps, prevState) {
    const { animationRequestId, animationPhase, mode } = this.state;

    // Start the animation in the next animation frame if the animation phase
    // has been reset. This is done because some browsers will only execute CSS3
    // animations if the properties change between frames.
    if (!animationRequestId && mode === ANIMATION_MODE && animationPhase === ANIMATION_INITIAL) {
      const animationRequestId = requestAnimationFrame(startAnimation.bind(this));
      this.setState({ animationRequestId });
    }

    // Refocus the Next button when a new item is shown.
    if (this.props.itemPackage.getId() !== prevProps.itemPackage.getId()) {
      this.navigationRef && this.navigationRef.focusDefaultButton(true);
    }

    // Refocus the Next whenever the screen returns to the default mode (e.g. after exiting spelling
    // practice, after closing the sentence tab, after the animation ends, etc.)
    if (this.state.mode === DEFAULT_MODE && prevState.mode !== DEFAULT_MODE) {
      this.navigationRef && this.navigationRef.focusDefaultButton();
    }
  }

  /**
   * If the item package changes, reset the state.
   */
  componentWillReceiveProps(nextProps) {
    const { animationRequestId } = this.state;

    if (this.props.itemPackage !== nextProps.itemPackage) {
      SoundManager.stop();
      cancelAnimationFrame(animationRequestId);
      this.setState(getInitialState.call(this));
    }
  }

  componentWillUnmount() {
    const { animationRequestId } = this.state;

    // Stop all sounds.
    SoundManager.stop();

    // Stop any animation/timeout that is happening.
    cancelAnimationFrame(animationRequestId);

    // Remove events from the window that were for the purpose of this instance.
    window.removeEventListener('keypress', this.handleKeypress);
    window.removeEventListener('keydown', this.handleKeydown);
  }

  render() {
    const { animationPhase, mode } = this.state;
    const {
      customNavigation,
      isNew,
      itemPackage,
      onRemove,
      showTranscription } = this.props;

    const navigationProps = {
      isNew,
      disableEnterKey: mode !== ANIMATION_MODE && mode !== DEFAULT_MODE && mode !== SENTENCE_MODE,
      // Don't allow switching the focus of the navigation buttons using the arrow keys while the
      // sentence tab is open, because the left/right arrow keys are used to switch between sentences
      // inside the sentence tab.
      disableArrowKeys: mode === SENTENCE_MODE || mode === SPELLING_MODE || mode === KANJI_STROKE_MODE,
      saveNavigationRef: this.saveNavigationRef,
    };
    const { transcription } = itemPackage.item.cue;
    const pos = itemPackage.getPartOfSpeech();

    // Classes to perform animations.
    const isAnimationMode = mode === ANIMATION_MODE;
    const screenContentClassName = classnames('screen-content', {
      animate: isAnimationMode,
      'animate-cue-initial': isAnimationMode && animationPhase === ANIMATION_CUE_INITIAL,
      'animate-play-sound-initial': isAnimationMode && animationPhase === ANIMATION_PLAY_SOUND_INITIAL,
      'animate-cue-fade': isAnimationMode && animationPhase === ANIMATION_CUE_FADE,
      'animate-sound-response': isAnimationMode && animationPhase === ANIMATION_SOUND_RESPONSE,
      'animate-response-fade': isAnimationMode && animationPhase === ANIMATION_RESPONSE_FADE,
      'animate-reveal-all': isAnimationMode && animationPhase === ANIMATION_REVEAL_ALL,
      'animate-play-sound': isAnimationMode && animationPhase === ANIMATION_PLAY_SOUND,
      'animate-shadowing': isAnimationMode && animationPhase === ANIMATION_SHADOWING,
      'animate-shadowing-fade': isAnimationMode && animationPhase === ANIMATION_SHADOWING_FADE,
    });

    // Determine what element the handleTransitionEnd callback should be placed on.
    const isCueAnimation = animationPhase === ANIMATION_CUE_INITIAL ||
                           animationPhase === ANIMATION_CUE_FADE ||
                           animationPhase === ANIMATION_REVEAL_ALL;

    const isResponseAnimation = animationPhase === ANIMATION_SOUND_RESPONSE ||
                                animationPhase === ANIMATION_RESPONSE_FADE;

    const isShadowAnimation = animationPhase === ANIMATION_SHADOWING ||
                              animationPhase === ANIMATION_SHADOWING_FADE;

    const onTransitionEnd = handleTransitionEnd.bind(this);

    return (
      <div className="screen screen-study-item">
        <div className={screenContentClassName}>
          <div className={classnames('clipped', { 'spelling-mode': mode === SPELLING_MODE })}>
            <div className={classnames('main', { 'hidden': mode === ANNOTATION_MODE || mode === KANJI_STROKE_MODE })}>
              {isNew && <span className="new-item-mark">{t('js_iknow.new item')}</span>}
              <p className="shadowing" onTransitionEnd={isShadowAnimation && onTransitionEnd}>{t('now you say it')}</p>

              <div className="cue-response">
                {showTranscription && transcription && <div className="transcription">{transcription}</div>}

                <div className="cue">
                  <div className="bottom">
                    <p className="content">
                      <span className="cue-container">
                        {itemPackage.sound &&
                          <span className="cue-sound">
                            <SoundPlayer source={itemPackage.sound} />
                            {' '}
                          </span>}

                        <FitText>
                          <span className="text-and-transliteration">
                            <span className="text" lang={itemPackage.item.cue.language} onTransitionEnd={isCueAnimation && onTransitionEnd} dangerouslySetInnerHTML={{ __html: itemPackage.getText() }} />
                            <span className="transliteration" dangerouslySetInnerHTML={{ __html: itemPackage.getTransliteration() }} />
                          </span>
                        </FitText>
                      </span>
                    </p>

                    {mode === SPELLING_MODE &&
                      <SpellingPractice
                        language={itemPackage.getLanguage()}
                        text={itemPackage.getTransliteration() || itemPackage.getText()}
                        sound={itemPackage.sound} />}
                  </div>
                </div>

                <div className="cue-response-separator"></div>

                {pos && <div className="part-of-speech">{partOfSpeech(pos)}</div>}

                <div className="response" onTransitionEnd={isResponseAnimation && onTransitionEnd}>
                  <p className="text">
                    <FitText><span dangerouslySetInnerHTML={{ __html: itemPackage.getTranslation() }} /></FitText>
                  </p>
                </div>

                <div className="actions">
                  {spellingModeEnabled.call(this) && <Glyph type={KEYBOARD} onClick={toggleFillInTheBlank.bind(this)} className="btn btn-icon btn-bar" title={t('spelling')} />}
                  {' '}
                  {kanjiStrokesEnabled.call(this) && <Glyph type={JAPANESE_CHARACTER} onClick={showKanjiStrokes.bind(this)} className="btn btn-icon btn-bar" title={t('stroke order')} />}
                  {' '}
                  {annotationsEnabled.call(this) && <Glyph type={PAPERCLIP} onClick={showAnnotations.bind(this)} className="btn btn-icon btn-bar" title={t('js_iknow.annotations')} />}
                </div>
              </div>

              {mode !== ANNOTATION_MODE && mode !== KANJI_STROKE_MODE && itemPackage.getSentences().length > 0 &&
                <SentenceTab
                  disableVimKeys={mode === SPELLING_MODE}
                  onOpen={showSentences.bind(this)}
                  onClose={setDefaultMode.bind(this)}
                  sentences={itemPackage.getSentences()} />}
            </div>

            {mode === KANJI_STROKE_MODE && <KanjiStrokes characters={itemPackage.getText()} onEscape={setDefaultMode.bind(this)} />}
            {mode === ANNOTATION_MODE && <Annotations annotations={itemPackage.getAnnotation()} onEscape={setDefaultMode.bind(this)} />}
          </div>
        </div>

        {customNavigation ?
          customNavigation(navigationProps) :
          <StudyScreenNavigation {...navigationProps} onNext={handleNext.bind(this)} onRemove={onRemove} />}
      </div>
    );
  }
}

/**
 * Used to determine if onNext and onRemove need to be set.
 */
function requiredNavigationFunction(props, propName) {
  if (!props.customNavigation && typeof props[propName] !== 'function') {
    return Error(`${propName} must be a function.`);
  }
}

StudyScreen.propTypes = {
  animateOnMount: PropTypes.bool,
  customNavigation: PropTypes.func,
  isNew: PropTypes.bool,
  itemPackage: PropTypes.object.isRequired,
  onNext: requiredNavigationFunction,
  onRemove: requiredNavigationFunction,
  playAudioOnMount: PropTypes.bool,
  showTranscription: PropTypes.bool,
};

StudyScreen.defaultProps = {
  animateOnMount: true,
  isNew: false,
  showTranscription: true,
};

export default StudyScreen;
